"use client";
import Image from "next/image";
import Link from "next/link";
import logo from "../../public/icons/logo.png";
import { newsletterSubscribe } from "@/services/publicService";
import { showError } from "@/utils/toast";
import { useContext, useState } from "react";
import UserContext from "@/context/userContext";
import LoginPopUpPage from "./LoginPopUp";

export default function Footer() {
  const [email, setEmail] = useState('');
  const context = useContext(UserContext);
  const user = context.user;
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [authInitialScreen, setAuthInitialScreen] = useState('welcome-back');
  // Mobile accordion: which footer column is expanded (defaults to first one
  // open, matching the design). On desktop the CSS forces all sections open.
  const [openSection, setOpenSection] = useState('explore');
  const toggleSection = (key) =>
    setOpenSection((prev) => (prev === key ? null : key));

  const openAuthPopup = (screen) => {
    setAuthInitialScreen(screen);
    setShowLoginPopup(true);
  };

  const ChevronIcon = ({ open }) => (
    <svg
      className={`footer-chevron ${open ? 'open' : ''}`}
      width="14"
      height="9"
      viewBox="0 0 14 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M1 1l6 6 6-6" stroke="#0D2469" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  // verify email
  const verifyEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // handle subscribe newsletter
  const handleSubscribeNewsletter = async () => {
    if(!verifyEmail(email)) {
      showError('Please enter a valid email');
      return;
    }
    try {
      const response = await newsletterSubscribe({ email: email });
      if(response.status === 200) {
        showSuccess(response.message);
        setEmail('');
      }
    } catch (error) {
      showError(error.response.data.message);
    }
  }

  return (
    <footer className="footer">
      <div className="container">
        <div className="row">
          <div className="col-lg-5 mb-4">
            <div className="footer-brand pe-5">
              <Image src={logo} alt="Invest in Energy" width={160} height={40} />
              <h3 className="logo-brand p-0">A 501(c)(3) nonprofit organization.</h3>
              <p className="brand-description p-0">
                Democratizing oil and gas investment. Empowering informed and ethical energy investors through knowledge and insight.
              </p>
            </div>
          </div>
          <div className="col-lg-7">
            <div className="row">
              <div className={`col-md-3 col-12 footer-col ${openSection === 'explore' ? 'is-open' : ''}`}>
                <h4 className="footer-heading" onClick={() => toggleSection('explore')}>
                  Explore<ChevronIcon open={openSection === 'explore'} />
                </h4>
                <ul className="footer-links">
                  <li><Link href="/">Home</Link></li>
                  <li><Link href="/about-us">About Us</Link></li>
                  <li><Link href="/partner-with-us">Partner With Us</Link></li>
                  <li><Link href="/learn-investing">Learn Investing</Link></li>
                  <li><Link href="/resources">Resources</Link></li>
                </ul>
              </div>

              <div className={`col-md-3 col-12 footer-col ${openSection === 'learn' ? 'is-open' : ''}`}>
                <h4 className="footer-heading" onClick={() => toggleSection('learn')}>
                  Learn Investing<ChevronIcon open={openSection === 'learn'} />
                </h4>
                <ul className="footer-links">
                  <li><Link href="/strategies-valuation">Strategies & Valuation</Link></li>
                  <li><Link href="/investment-types">Investment Types</Link></li>
                  <li><Link href="/glossary">Glossary</Link></li>
                </ul>
              </div>

              <div className={`col-md-3 col-12 footer-col ${openSection === 'insights' ? 'is-open' : ''}`}>
                <h4 className="footer-heading" onClick={() => toggleSection('insights')}>
                  Insights<ChevronIcon open={openSection === 'insights'} />
                </h4>
                <ul className="footer-links">
                  <li><Link href="/articles">Articles</Link></li>
                  <li><Link href="/all-news">News</Link></li>
                  <li><Link href="/market-insights">Market Insight</Link></li>
                </ul>
              </div>

              <div className={`col-md-3 col-12 footer-col ${openSection === 'account' ? 'is-open' : ''}`}>
                <h4 className="footer-heading" onClick={() => toggleSection('account')}>
                  Account<ChevronIcon open={openSection === 'account'} />
                </h4>
                <ul className="footer-links">
                  {user ? (
                    <li><Link href="/profile">Sign Up</Link></li>
                  ) : (
                    <li onClick={() => openAuthPopup('join-invest')} style={{ cursor: 'pointer' }}>Sign Up</li>
                  )}
                  {user ? (
                    <li><Link href="/profile">Profile</Link></li>
                  ) : (
                    <li onClick={() => openAuthPopup('welcome-back')} style={{ cursor: 'pointer' }}>Profile</li>
                  )}
                  {user ? (
                    <li><Link href="/become-author">Become an Author</Link></li>
                  ) : (
                    <li onClick={() => openAuthPopup('welcome-back')} style={{ cursor: 'pointer' }}>Become an Author</li>
                  )}
                  <li><Link href="/contact-us">Contact Us</Link></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-12">
            <div id="newsletter-subscribe-mi" className="newsletter-section">
              <p className="newsletter-text">
                Subscribe and get a free 10-page guide: <strong>"How to Start Investing in Oil & Gas"</strong>
              </p>
              <div className="newsletter-form">
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-control newsletter-input" 
                  placeholder="Enter your email"
                />
                <button className="btn btn-primary newsletter-btn" onClick={handleSubscribeNewsletter}>Subscribe</button>
              </div>
              <p className="newsletter-subtext">Subscribe to be the first to get updates.</p>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="row align-items-center">
            <div className="col-md-4">
              <p className="copyright">© 2026 All Rights Reserved.</p>
            </div>
            <div className="col-md-4">
              <div className="footer-legal">
                <Link href="/privacy-policy">Privacy Policy</Link>|
                <Link href="/editorial-guidelines">Editorial Guidelines</Link>|
                <Link href="/terms-and-conditions">Terms & Conditions</Link>
              </div>
            </div>
            <div className="col-md-4">
              <div className="social-icons">
                <Link href="https://www.facebook.com/investinenergyy/" target="_blank" aria-label="Facebook">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 12.3038C22 6.74719 17.5229 2.24268 12 2.24268C6.47715 2.24268 2 6.74719 2 12.3038C2 17.3255 5.65684 21.4879 10.4375 22.2427V15.2121H7.89844V12.3038H10.4375V10.0872C10.4375 7.56564 11.9305 6.1728 14.2146 6.1728C15.3088 6.1728 16.4531 6.36931 16.4531 6.36931V8.84529H15.1922C13.95 8.84529 13.5625 9.6209 13.5625 10.4166V12.3038H16.3359L15.8926 15.2121H13.5625V22.2427C18.3432 21.4879 22 17.3257 22 12.3038Z" fill="#0C0C0C" />
                  </svg>

                </Link>
                <Link href="https://x.com/investinenergy_" target="_blank" aria-label="Twitter">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.1761 4.24268H19.9362L13.9061 11.0201L21 20.2427H15.4456L11.0951 14.6493L6.11723 20.2427H3.35544L9.80517 12.9935L3 4.24268H8.69545L12.6279 9.3553L17.1761 4.24268ZM16.2073 18.6181H17.7368L7.86441 5.78196H6.2232L16.2073 18.6181Z" fill="#0C0C0C" />
                  </svg>

                </Link>
                <Link href="https://www.linkedin.com/company/invest-in-energy/about" target="_blank" aria-label="LinkedIn">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M4.5 3.24268C3.67157 3.24268 3 3.91425 3 4.74268V19.7427C3 20.5711 3.67157 21.2427 4.5 21.2427H19.5C20.3284 21.2427 21 20.5711 21 19.7427V4.74268C21 3.91425 20.3284 3.24268 19.5 3.24268H4.5ZM8.52076 7.2454C8.52639 8.20165 7.81061 8.79087 6.96123 8.78665C6.16107 8.78243 5.46357 8.1454 5.46779 7.24681C5.47201 6.40165 6.13998 5.72243 7.00764 5.74212C7.88795 5.76181 8.52639 6.40728 8.52076 7.2454ZM12.2797 10.0044H9.75971H9.7583V18.5643H12.4217V18.3646C12.4217 17.9847 12.4214 17.6047 12.4211 17.2246C12.4203 16.2108 12.4194 15.1959 12.4246 14.1824C12.426 13.9363 12.4372 13.6804 12.5005 13.4455C12.7381 12.568 13.5271 12.0013 14.4074 12.1406C14.9727 12.2291 15.3467 12.5568 15.5042 13.0898C15.6013 13.423 15.6449 13.7816 15.6491 14.129C15.6605 15.1766 15.6589 16.2242 15.6573 17.2719C15.6567 17.6417 15.6561 18.0117 15.6561 18.3815V18.5629H18.328V18.3576C18.328 17.9056 18.3278 17.4537 18.3275 17.0018C18.327 15.8723 18.3264 14.7428 18.3294 13.6129C18.3308 13.1024 18.276 12.599 18.1508 12.1054C17.9638 11.3713 17.5771 10.7638 16.9485 10.3251C16.5027 10.0129 16.0133 9.81178 15.4663 9.78928C15.404 9.78669 15.3412 9.7833 15.2781 9.77989C14.9984 9.76477 14.7141 9.74941 14.4467 9.80334C13.6817 9.95662 13.0096 10.3068 12.5019 10.9241C12.4429 10.9949 12.3852 11.0668 12.2991 11.1741L12.2797 11.1984V10.0044ZM5.68164 18.5671H8.33242V10.01H5.68164V18.5671Z" fill="#0C0C0C" />
                  </svg>

                </Link>
                <Link href="https://www.instagram.com/investinenergy" target="_blank" aria-label="Instagram">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 3C17.3261 3 18.5979 3.52678 19.5355 4.46447C20.4732 5.40215 21 6.67392 21 8V16C21 17.3261 20.4732 18.5979 19.5355 19.5355C18.5979 20.4732 17.3261 21 16 21H8C6.67392 21 5.40215 20.4732 4.46447 19.5355C3.52678 18.5979 3 17.3261 3 16V8C3 6.67392 3.52678 5.40215 4.46447 4.46447C5.40215 3.52678 6.67392 3 8 3H16ZM12 8C10.9391 8 9.92172 8.42143 9.17157 9.17157C8.42143 9.92172 8 10.9391 8 12C8 13.0609 8.42143 14.0783 9.17157 14.8284C9.92172 15.5786 10.9391 16 12 16C13.0609 16 14.0783 15.5786 14.8284 14.8284C15.5786 14.0783 16 13.0609 16 12C16 10.9391 15.5786 9.92172 14.8284 9.17157C14.0783 8.42143 13.0609 8 12 8ZM12 10C12.5304 10 13.0391 10.2107 13.4142 10.5858C13.7893 10.9609 14 11.4696 14 12C14 12.5304 13.7893 13.0391 13.4142 13.4142C13.0391 13.7893 12.5304 14 12 14C11.4696 14 10.9609 13.7893 10.5858 13.4142C10.2107 13.0391 10 12.5304 10 12C10 11.4696 10.2107 10.9609 10.5858 10.5858C10.9609 10.2107 11.4696 10 12 10ZM16.5 6.5C16.2348 6.5 15.9804 6.60536 15.7929 6.79289C15.6054 6.98043 15.5 7.23478 15.5 7.5C15.5 7.76522 15.6054 8.01957 15.7929 8.20711C15.9804 8.39464 16.2348 8.5 16.5 8.5C16.7652 8.5 17.0196 8.39464 17.2071 8.20711C17.3946 8.01957 17.5 7.76522 17.5 7.5C17.5 7.23478 17.3946 6.98043 17.2071 6.79289C17.0196 6.60536 16.7652 6.5 16.5 6.5Z" fill="#0C0C0C"/>
                </svg>


                </Link>
              </div>
            </div>
          </div>
        </div>
        {showLoginPopup && (
              <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 99999, overflow: 'auto' }}>
                <LoginPopUpPage onClose={() => setShowLoginPopup(false)} initialScreen={authInitialScreen} />
              </div>
            )}
      </div>
    </footer>
  );
}

